import { useCallback, useEffect, useRef, useState } from "react";

// Shrinks the observed viewport to a band around vertical center, so "in
// view" means "roughly centered on screen" rather than "any pixel visible" —
// the same trick short-form video feeds use to settle on one active item
// instead of two adjacent cards both counting as visible.
const CENTER_BAND_ROOT_MARGIN = "-35% 0px -35% 0px";
const THRESHOLDS = [0, 0.25, 0.5, 0.75, 1];

// Tracks which registered card is most centered in the viewport and exposes
// a per-key ref callback to observe it. Built for a single scrolling list —
// one shared IntersectionObserver instead of one per card — so "next clip
// autoplays as the previous one leaves view" falls out of the same
// ratio-comparison used to pick the current winner.
export const useAutoplayObserver = () => {
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const activeKeyRef = useRef<string | null>(null);
  const ratiosByKeyRef = useRef(new Map<string, number>());
  const keyByNodeRef = useRef(new Map<Element, string>());
  const observerRef = useRef<IntersectionObserver | null>(null);
  const refCallbacksRef = useRef(
    new Map<string, (node: HTMLElement | null) => void>(),
  );

  const pickWinner = useCallback(() => {
    let winnerKey: string | null = null;
    let winnerRatio = 0;
    ratiosByKeyRef.current.forEach((ratio, key) => {
      if (ratio > winnerRatio) {
        winnerRatio = ratio;
        winnerKey = key;
      }
    });
    if (winnerKey !== activeKeyRef.current) {
      activeKeyRef.current = winnerKey;
      setActiveKey(winnerKey);
    }
  }, []);

  // Lazily created (not effect-created) so it exists in time for ref
  // callbacks fired during the same commit as the initial render.
  const getObserver = useCallback(() => {
    if (!observerRef.current && typeof IntersectionObserver !== "undefined") {
      observerRef.current = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const key = keyByNodeRef.current.get(entry.target);
            if (!key) return;
            if (entry.isIntersecting) {
              ratiosByKeyRef.current.set(key, entry.intersectionRatio);
            } else {
              ratiosByKeyRef.current.delete(key);
            }
          });
          pickWinner();
        },
        { threshold: THRESHOLDS, rootMargin: CENTER_BAND_ROOT_MARGIN },
      );
    }
    return observerRef.current;
  }, [pickWinner]);

  useEffect(
    () => () => {
      observerRef.current?.disconnect();
      observerRef.current = null;
    },
    [],
  );

  const registerRef = useCallback(
    (key: string) => {
      const cached = refCallbacksRef.current.get(key);
      if (cached) return cached;

      const callback = (node: HTMLElement | null) => {
        const observer = getObserver();
        if (!observer) return;

        if (node) {
          keyByNodeRef.current.set(node, key);
          observer.observe(node);
          return;
        }

        // React calls this with null right before unmount/reattach — find
        // and drop whatever node was previously registered under this key.
        for (const [el, k] of keyByNodeRef.current) {
          if (k === key) {
            observer.unobserve(el);
            keyByNodeRef.current.delete(el);
            break;
          }
        }
        ratiosByKeyRef.current.delete(key);
        pickWinner();
      };

      refCallbacksRef.current.set(key, callback);
      return callback;
    },
    [getObserver, pickWinner],
  );

  return { activeKey, registerRef };
};
